/* eslint-disable no-undef */
import React, { useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../config/authConfig';
import { callMsGraph } from './graph';
import { useDispatch, useSelector } from 'react-redux';
import {
    setUserDetails,
    setIsAuthenticatedWithHybris,
    login,
} from 'src/common/redux/actions';
import { validateUserRole, checkImpersonationActive, getWcmsHostName } from 'src/utils/util';
import { getAuthConfig } from 'src/utils/authConfig';
import { useHistory } from 'react-router-dom';
import { getUrlparams } from 'src/utils/util.js';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import { DCA_USER_ROLE, RCM_USER_ROLE, SP_USER_ROLE, RVP_USER_ROLE } from 'src/constants/appConstants';
import {
    SSO_ERROR_USER_NOT_ACTIVE,
    SSO_ERROR_ACCESS_DENIED,
    SSO_ERROR_GENERIC,
} from 'src/common/redux/actions/constants';
import { InactiveUserPage, AccessDeniedPage, GenericErrorPage } from './sso-error';

// eslint-disable-next-line no-unused-vars
const AccessToken = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const isAuthenticated = useIsAuthenticated();
    const { instance, accounts } = useMsal();
    const [, setAccessToken] = useState(null);
    const hybrisUserDetails = useSelector(
        (state) => state.loginReducer.hybrisUserDetails
    );
    const ssoError = useSelector((state) => state.loginReducer.ssoError);
    const ssoErrorType = useSelector((state) => state.loginReducer.ssoErrorType);

    useEffectWithErrorLogging(() => {
        if (isAuthenticated) {
            const request = {
                ...loginRequest,
                account: accounts[0],
            };

            instance
                .acquireTokenSilent(request)
                .then((response) => {
                    setAccessToken(response.accessToken);
                    // On-prem: store MSAL token so interceptor sends it as Bearer for token API
                    if (!getAuthConfig().useBasicAuth) {
                        localStorage.setItem('authToken', response.accessToken);
                    }
                    dispatch(login({
                        ssoToken: response.accessToken,
                        isLoginFlow: false,
                    }));
                })
                .catch(() => {
                    instance.acquireTokenPopup(request).then((response) => {
                        setAccessToken(response.accessToken);
                        callMsGraph(response.accessToken).then((response) => {
                            dispatch(setUserDetails(response));
                        });
                    });
                });
        }
    }, [isAuthenticated]);

    let query = getUrlparams();
    useEffectWithErrorLogging(() => {
        const principalUser =
            sessionStorage.getItem('impersonatedUserId') === null
                ? 'null'
                : sessionStorage.getItem('impersonatedUserId'); // id from session
        const impersonatedUserId =
            query.get('impersonatedUser') === null
                ? 'null'
                : query.get('impersonatedUser'); // id from url

        if (
            hybrisUserDetails.employeeRole ||
            (principalUser !== 'null' && impersonatedUserId !== 'null')
        ) {
            if (
                validateUserRole(hybrisUserDetails.employeeRole) ||
                (principalUser !== 'null' && impersonatedUserId !== 'null')
            ) {
                dispatch(setIsAuthenticatedWithHybris(true));
                localStorage.setItem('userAuthenticated', true);
                if (
                    principalUser !== 'null' &&
                    impersonatedUserId !== 'null' &&
                    !checkImpersonationActive()
                ) {
                    history.push(
                        `/ImpersonationLanding?impersonatedUser=${principalUser}`
                    );
                } else if (
                    principalUser !== 'null' &&
                    impersonatedUserId !== 'null' &&
                    checkImpersonationActive()
                ) {
                    // don't do any redirection
                } else if (hybrisUserDetails.employeeRole === DCA_USER_ROLE || hybrisUserDetails.employeeRole === RCM_USER_ROLE || hybrisUserDetails.employeeRole === SP_USER_ROLE || hybrisUserDetails.employeeRole === RVP_USER_ROLE) {
                    // Only redirect to overrides when no impersonation session is in progress
                    if (principalUser === 'null' && !checkProxyActive()) {
                        history.push('/overrides');
                    }
                } else {
                    // history.push('/');
                }
            } else {
                window.location.href = getWcmsHostName();
            }
        } else {
            dispatch(setIsAuthenticatedWithHybris(false));
            localStorage.removeItem('userAuthenticated');
        }
    }, [hybrisUserDetails]);

    // Handle SSO errors - show appropriate error page
    if (ssoError && ssoErrorType) {
        switch (ssoErrorType) {
            case SSO_ERROR_USER_NOT_ACTIVE:
                return <InactiveUserPage errorMessage={ssoError} />;
            case SSO_ERROR_ACCESS_DENIED:
                return <AccessDeniedPage errorMessage={ssoError} />;
            case SSO_ERROR_GENERIC:
            default:
                return <GenericErrorPage errorMessage={ssoError} />;
        }
    }

    return '';
};

export default AccessToken;
